import React, { useEffect, useState } from "react";
import axios from "axios";
import { FaSyncAlt, FaEdit, FaTrash, FaPlus } from "react-icons/fa";
import { Button, Modal, Table, Spinner, Form } from "react-bootstrap";
import { REACT_APP_API_URL } from "../../../../../env";

const CURRENT_USER_ID = 1;
const API = `${REACT_APP_API_URL}api/tax-config`;

const SubTaxTypeMaster = () => {
  const [subTaxTypes, setSubTaxTypes] = useState([]);
  const [listLoad, setListLoad] = useState(false);
  const [listErr, setListErr] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [subTaxName, setSubTaxName] = useState("");
  const [formLoad, setFormLoad] = useState(false);
  const [formErr, setFormErr] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoad, setDeleteLoad] = useState(false);

  const loadSubTaxTypes = async () => {
    try {
      setListLoad(true);
      setListErr(null);
      const res = await axios.get(`${API}/sub-tax-types-master`);
      setSubTaxTypes(res.data.data || []);
    } catch (err) {
      setListErr(err.response?.data?.message || "Could not load sub tax types.");
    } finally {
      setListLoad(false);
    }
  };

  useEffect(() => {
    loadSubTaxTypes();
  }, []);

  const openAdd = () => {
    setEditingId(null);
    setSubTaxName("");
    setFormErr(null);
    setShowForm(true);
  };

  const openEdit = (row) => {
    setEditingId(row.SubtaxId);
    setSubTaxName(row.SubTaxType);
    setFormErr(null);
    setShowForm(true);
  };

  const handleSave = async () => {
    const name = subTaxName.trim();
    if (!name) return setFormErr("Sub tax type name is required");

    try {
      setFormLoad(true);
      setFormErr(null);
      if (editingId) {
        await axios.put(`${API}/sub-tax-types-master/${editingId}`, {
          subTaxType: name,
          updatedBy: CURRENT_USER_ID
        });
      } else {
        await axios.post(`${API}/sub-tax-types-master`, {
          subTaxType: name,
          createdBy: CURRENT_USER_ID
        });
      }
      setShowForm(false);
      loadSubTaxTypes();
    } catch (err) {
      setFormErr(err.response?.data?.message || "Could not save sub tax type.");
    } finally {
      setFormLoad(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      setDeleteLoad(true);
      await axios.delete(`${API}/sub-tax-types-master/${deleteId}`, {
        params: { updatedBy: CURRENT_USER_ID }
      });
      setDeleteId(null);
      loadSubTaxTypes();
    } catch (err) {
      setListErr(err.response?.data?.message || "Could not delete sub tax type.");
      setDeleteId(null);
    } finally {
      setDeleteLoad(false);
    }
  };

  return (
    <div className="card mt-4">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <h3 className="mb-0">Sub Tax Type Master</h3>
          <div className="d-flex gap-2">
            <Button variant="outline-secondary" onClick={loadSubTaxTypes} disabled={listLoad}>
              <FaSyncAlt className={listLoad ? "fa-spin" : ""} /> Refresh
            </Button>
            <Button variant="primary" onClick={openAdd}>
              <FaPlus className="me-1" /> Add Sub Tax Type
            </Button>
          </div>
        </div>

        {listErr && <div className="alert alert-danger py-2">{listErr}</div>}

        <div className="table-responsive">
          <Table bordered hover className="align-middle">
            <thead className="text-center">
              <tr>
                <th>#</th>
                <th>Sub Tax Type</th>
                <th>Created On</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {listLoad ? (
                <tr>
                  <td colSpan={4} className="text-center py-4">
                    <Spinner animation="border" size="sm" /> Loading...
                  </td>
                </tr>
              ) : subTaxTypes.length > 0 ? (
                subTaxTypes.map((row, index) => (
                  <tr key={row.SubtaxId}>
                    <td className="text-center">{index + 1}</td>
                    <td>{row.SubTaxType}</td>
                    <td className="text-center">{row.CreatedOn || "-"}</td>
                    <td className="text-center">
                      <div className="d-flex justify-content-center gap-1">
                        <Button size="sm" variant="outline-secondary" onClick={() => openEdit(row)} title="Edit">
                          <FaEdit />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => setDeleteId(row.SubtaxId)}
                          disabled={row.SubtaxId === 1 || row.SubtaxId === 2}
                          title={row.SubtaxId === 1 || row.SubtaxId === 2 ? "CGST/SGST cannot be deleted" : "Delete"}
                        >
                          <FaTrash />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="text-center text-muted py-4">
                    No sub tax types found.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>

      <Modal show={showForm} onHide={() => setShowForm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editingId ? "Edit Sub Tax Type" : "Add Sub Tax Type"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label className="fw-semibold">Sub Tax Type Name</Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g. CGST, SGST, IGST"
              value={subTaxName}
              onChange={(e) => setSubTaxName(e.target.value)}
              disabled={formLoad}
            />
          </Form.Group>
          {formErr && <div className="alert alert-danger py-2 mt-3 mb-0">{formErr}</div>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowForm(false)} disabled={formLoad}>
            Cancel
          </Button>
          <Button variant="dark" onClick={handleSave} disabled={formLoad}>
            {formLoad ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />Saving...
              </>
            ) : editingId ? (
              "Update"
            ) : (
              "Add"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!deleteId} onHide={() => setDeleteId(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete Sub Tax Type?</Modal.Title>
        </Modal.Header>
        <Modal.Body>This sub tax type will be deleted. This action cannot be undone.</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteId(null)} disabled={deleteLoad}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleteLoad}>
            {deleteLoad ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />Deleting...
              </>
            ) : (
              "Delete"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SubTaxTypeMaster;
